import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import PageHeader from '../components/PageHeader'
import TableFilters from '../components/TableFilters'
import AddServiceModal from '../components/AddServiceModal'
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const STAGES = ['First Call Done', 'Discovery Call Done', 'Proposal Sent', 'Negotiation', 'Won', 'Lost']
const KANBAN_STAGES = ['First Call Done', 'Discovery Call Done', 'Proposal Sent', 'Negotiation']

const DEMO = []

const EMPTY = { client_name:'', contact:'', service_interest:'', stage:'First Call Done', estimated_value:'', assigned_to:'', follow_up_date:'', requirements:'', requirements_due:'', requirements_done:false, remarks:'' }

function stageBadge(s) {
  const map = { 'First Call Done':'badge-gray', 'Discovery Call Done':'badge-blue', 'Proposal Sent':'badge-purple', 'Negotiation':'badge-amber', 'Won':'badge-green', 'Lost':'badge-red' }
  return 'badge ' + (map[s] || 'badge-gray')
}

function fmt(n) { return '₹' + Number(n).toLocaleString('en-IN') }
function today() { return new Date().toISOString().slice(0, 10) }

// Draggable kanban card
function KanbanCard({ lead, onEdit, onContextAction }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lead.id, data: { stage: lead.stage } })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    background:'var(--surface)',
    border:'1px solid var(--border)',
    borderRadius:8,
    padding:12,
    marginBottom:8,
    cursor:'grab',
    position:'relative',
  }

  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div onClick={() => onEdit(lead)} style={{ cursor:'pointer' }}>
        <div style={{ fontWeight:500, fontSize:13, marginBottom:3 }}>{lead.client_name}</div>
        <div style={{ fontSize:11, color:'var(--text3)', marginBottom:8 }}>{lead.service_interest}</div>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text2)' }}>
          <span>{fmt(lead.estimated_value)}</span>
          <span>{lead.assigned_to}</span>
        </div>
        {lead.follow_up_date && <div style={{ fontSize:10, color:'var(--text3)', marginTop:6 }}>Follow-up: {lead.follow_up_date}</div>}
        {lead.requirements && (
          <div style={{ fontSize:10, marginTop:6, display:'flex', alignItems:'center', gap:4 }}>
            <span className={lead.requirements_done ? 'badge badge-green' : 'badge badge-amber'} style={{ fontSize:9, padding:'1px 6px' }}>
              {lead.requirements_done ? 'Req done' : 'Req pending'}
            </span>
            {lead.requirements_due && !lead.requirements_done && (
              <span style={{ color:'var(--text3)' }}>Due: {lead.requirements_due}</span>
            )}
          </div>
        )}
      </div>
      {/* Three-dot menu */}
      <div style={{ position:'absolute', top:8, right:8 }}>
        <button
          onClick={e => { e.stopPropagation(); setMenuOpen(!menuOpen) }}
          onMouseDown={e => e.stopPropagation()}
          style={{ background:'none', border:'none', cursor:'pointer', padding:'2px 6px', fontSize:16, color:'var(--text3)', lineHeight:1 }}
        >⋮</button>
        {menuOpen && (
          <div
            style={{
              position:'absolute', top:'100%', right:0, background:'var(--surface)', border:'1px solid var(--border)',
              borderRadius:6, boxShadow:'var(--shadow)', zIndex:10, minWidth:120, overflow:'hidden'
            }}
            onMouseLeave={() => setMenuOpen(false)}
          >
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); setMenuOpen(false); onContextAction(lead, 'won') }}
              style={{ display:'block', width:'100%', padding:'8px 12px', border:'none', background:'none', cursor:'pointer', fontSize:12, textAlign:'left', color:'var(--success)' }}
            >Mark as Won</button>
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); setMenuOpen(false); onContextAction(lead, 'lost') }}
              style={{ display:'block', width:'100%', padding:'8px 12px', border:'none', background:'none', cursor:'pointer', fontSize:12, textAlign:'left', color:'var(--danger)' }}
            >Mark as Lost</button>
          </div>
        )}
      </div>
    </div>
  )
}

// Droppable column
function KanbanColumn({ stage, items, onEdit, onContextAction }) {
  return (
    <SortableContext items={items.map(l => l.id)} strategy={verticalListSortingStrategy}>
      <div
        style={{ background:'var(--surface2)', borderRadius:10, padding:12, minHeight:120 }}
        data-stage={stage}
      >
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <span style={{ fontSize:12, fontWeight:600, color:'var(--text2)' }}>{stage}</span>
          <span className="badge badge-gray">{items.length}</span>
        </div>
        {items.map(l => (
          <KanbanCard key={l.id} lead={l} onEdit={onEdit} onContextAction={onContextAction} />
        ))}
        {items.length === 0 && <div style={{ textAlign:'center', color:'var(--text3)', fontSize:12, padding:'20px 0' }}>No leads</div>}
      </div>
    </SortableContext>
  )
}

export default function Pipeline() {
  const [leads, setLeads] = useState(DEMO)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [view, setView] = useState('kanban')
  const [filtered, setFiltered] = useState([])
  const [activeId, setActiveId] = useState(null)

  // Conversion modal state — now uses AddServiceModal
  const [convertModal, setConvertModal] = useState(false)
  const [convertLead, setConvertLead] = useState(null)
  const [convertSuccess, setConvertSuccess] = useState(false)

  const TABLE_FILTERS = [
    { key:'search', label:'Search', type:'text', fields:['client_name','service_interest','contact'] },
    { key:'stage', label:'Stage', type:'select', options: STAGES },
    { key:'assigned_to', label:'Assigned', type:'select', options: [...new Set(leads.map(l => l.assigned_to).filter(Boolean))] },
  ]
  const handleFiltered = useCallback(d => setFiltered(d), [])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  useEffect(() => {
    supabase.from('pipeline').select('*').order('date_added', { ascending:false }).then(({ data }) => { if (data?.length) setLeads(data) })
  }, [])

  function openAdd() { setForm(EMPTY); setEditing(null); setModal(true) }
  function openEdit(l) { setForm(l); setEditing(l.id); setModal(true) }

  async function save() {
    setSaving(true)
    const payload = { ...form, estimated_value: Number(form.estimated_value) || 0 }
    if (editing) {
      const { data } = await supabase.from('pipeline').update(payload).eq('id', editing).select()
      setLeads(prev => prev.map(l => l.id === editing ? (data?.[0] || { ...l, ...payload }) : l))
    } else {
      const { data } = await supabase.from('pipeline').insert([payload]).select()
      setLeads(prev => [...prev, data?.[0] || { ...payload, id: Date.now() }])
    }
    setSaving(false); setModal(false)
  }

  async function remove(id) {
    if (!window.confirm('Delete this lead?')) return
    await supabase.from('pipeline').delete().eq('id', id)
    setLeads(prev => prev.filter(l => l.id !== id))
  }

  // DnD handlers
  function handleDragStart(event) { setActiveId(event.active.id) }

  async function handleDragEnd(event) {
    setActiveId(null)
    const { active, over } = event
    if (!over) return

    // Find which column was dropped into
    const overElement = document.elementFromPoint(event.activatorEvent.clientX, event.activatorEvent.clientY)
    let targetStage = null

    // Check if dropped over another card — use that card's stage
    const overLead = leads.find(l => l.id === over.id)
    if (overLead) {
      targetStage = overLead.stage
    }

    // Or find the column container
    if (!targetStage) {
      const cols = document.querySelectorAll('[data-stage]')
      const rect = over.rect
      cols.forEach(col => {
        const cr = col.getBoundingClientRect()
        if (rect && rect.current && rect.current.translated) {
          const cx = rect.current.translated.left
          const cy = rect.current.translated.top
          if (cx >= cr.left && cx <= cr.right && cy >= cr.top && cy <= cr.bottom) {
            targetStage = col.getAttribute('data-stage')
          }
        }
      })
    }

    if (!targetStage) return

    const draggedLead = leads.find(l => l.id === active.id)
    if (!draggedLead || draggedLead.stage === targetStage) return

    // Update in DB and state
    const { data } = await supabase.from('pipeline').update({ stage: targetStage }).eq('id', active.id).select()
    setLeads(prev => prev.map(l => l.id === active.id ? (data?.[0] || { ...l, stage: targetStage }) : l))
  }

  // Context menu actions
  function handleContextAction(lead, action) {
    if (action === 'won') markWon(lead)
    else if (action === 'lost') markLost(lead)
  }

  async function markWon(lead) {
    const { data } = await supabase.from('pipeline').update({ stage: 'Won' }).eq('id', lead.id).select()
    const updated = data?.[0] || { ...lead, stage: 'Won' }
    setLeads(prev => prev.map(l => l.id === lead.id ? updated : l))
    setConvertLead(updated)
    setConvertSuccess(false)
    setConvertModal(true)
  }

  async function markLost(lead) {
    if (!window.confirm(`Mark "${lead.client_name}" as Lost?`)) return
    const { data } = await supabase.from('pipeline').update({ stage: 'Lost' }).eq('id', lead.id).select()
    setLeads(prev => prev.map(l => l.id === lead.id ? (data?.[0] || { ...l, stage: 'Lost' }) : l))
  }

  function onServiceCreated(service) {
    setConvertSuccess(true)
  }

  // Handle stage change from table dropdown
  async function handleStageChange(lead, newStage) {
    if (newStage === 'Won') { markWon(lead); return }
    if (newStage === 'Lost') { markLost(lead); return }
    const { data } = await supabase.from('pipeline').update({ stage: newStage }).eq('id', lead.id).select()
    setLeads(prev => prev.map(l => l.id === lead.id ? (data?.[0] || { ...l, stage: newStage }) : l))
  }

  const totalValue = leads.filter(l => !['Won','Lost'].includes(l.stage)).reduce((s, l) => s + (Number(l.estimated_value) || 0), 0)
  const wonLeads = leads.filter(l => l.stage === 'Won')
  const lostLeads = leads.filter(l => l.stage === 'Lost')

  // Conversion stats for current month
  const currentMonth = today().slice(0, 7)
  const thisMonthLeads = leads.filter(l => (l.date_added || '').startsWith(currentMonth))
  const thisMonthWon = thisMonthLeads.filter(l => l.stage === 'Won').length
  const conversionRate = thisMonthLeads.length > 0 ? Math.round((thisMonthWon / thisMonthLeads.length) * 100) : 0

  const activeLead = leads.find(l => l.id === activeId)

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1 }}>
      <PageHeader
        title="Pipeline"
        subtitle={`${leads.length} leads · ${fmt(totalValue)} pipeline value`}
        action={<button className="btn btn-primary" onClick={openAdd}>+ Add lead</button>}
      />
      <div style={{ padding:28, flex:1 }}>

        {/* Conversion stats bar */}
        <div style={{ display:'flex', gap:20, marginBottom:16, padding:'10px 16px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, fontSize:12, color:'var(--text2)' }}>
          <span>This month: <strong style={{ color:'var(--text)' }}>{thisMonthLeads.length}</strong> new leads</span>
          <span>Converted: <strong style={{ color:'var(--success)' }}>{thisMonthWon}</strong></span>
          <span>Rate: <strong style={{ color: conversionRate >= 50 ? 'var(--success)' : 'var(--warning)' }}>{conversionRate}%</strong></span>
        </div>

        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          <button className={`btn${view==='kanban' ? ' btn-primary' : ''}`} onClick={() => setView('kanban')}>Kanban</button>
          <button className={`btn${view==='table' ? ' btn-primary' : ''}`} onClick={() => setView('table')}>Table</button>
        </div>

        {view === 'kanban' && (
          <>
            <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
                {KANBAN_STAGES.map(stage => {
                  const items = leads.filter(l => l.stage === stage)
                  return <KanbanColumn key={stage} stage={stage} items={items} onEdit={openEdit} onContextAction={handleContextAction} />
                })}
              </div>
              <DragOverlay>
                {activeLead ? (
                  <div style={{ background:'var(--surface)', border:'2px solid var(--accent)', borderRadius:8, padding:12, boxShadow:'0 8px 24px rgba(0,0,0,0.15)', opacity:0.9, width:240 }}>
                    <div style={{ fontWeight:500, fontSize:13 }}>{activeLead.client_name}</div>
                    <div style={{ fontSize:11, color:'var(--text3)' }}>{activeLead.service_interest}</div>
                    <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>{fmt(activeLead.estimated_value)}</div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>

            {/* Won & Lost sections */}
            {(wonLeads.length > 0 || lostLeads.length > 0) && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:16 }}>
                <div style={{ background:'var(--success-bg)', borderRadius:10, padding:12 }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                    <span style={{ fontSize:12, fontWeight:600, color:'var(--success)' }}>Won</span>
                    <span className="badge badge-green">{wonLeads.length}</span>
                  </div>
                  {wonLeads.map(l => (
                    <div key={l.id} onClick={() => openEdit(l)} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:10, marginBottom:6, cursor:'pointer', fontSize:12 }}>
                      <span style={{ fontWeight:500 }}>{l.client_name}</span>
                      <span style={{ float:'right', color:'var(--text2)' }}>{fmt(l.estimated_value)}</span>
                    </div>
                  ))}
                  {wonLeads.length === 0 && <div style={{ textAlign:'center', color:'var(--text3)', fontSize:12, padding:'12px 0' }}>No won leads yet</div>}
                </div>
                <div style={{ background:'var(--danger-bg)', borderRadius:10, padding:12 }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                    <span style={{ fontSize:12, fontWeight:600, color:'var(--danger)' }}>Lost</span>
                    <span className="badge badge-red">{lostLeads.length}</span>
                  </div>
                  {lostLeads.map(l => (
                    <div key={l.id} onClick={() => openEdit(l)} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:10, marginBottom:6, cursor:'pointer', fontSize:12 }}>
                      <span style={{ fontWeight:500 }}>{l.client_name}</span>
                      <span style={{ float:'right', color:'var(--text2)' }}>{fmt(l.estimated_value)}</span>
                    </div>
                  ))}
                  {lostLeads.length === 0 && <div style={{ textAlign:'center', color:'var(--text3)', fontSize:12, padding:'12px 0' }}>No lost leads</div>}
                </div>
              </div>
            )}
          </>
        )}

        {view === 'table' && (
          <>
            <TableFilters filters={TABLE_FILTERS} data={leads} onFiltered={handleFiltered} />
            <div className="card">
              <table>
                <thead><tr><th>Client</th><th>Service interest</th><th>Stage</th><th>Est. value</th><th>Assigned</th><th>Requirements</th><th>Follow-up</th><th></th></tr></thead>
                <tbody>
                  {filtered.map(l => (
                    <tr key={l.id}>
                      <td style={{ fontWeight:500 }}>{l.client_name}</td>
                      <td>{l.service_interest}</td>
                      <td>
                        <select
                          className={stageBadge(l.stage)}
                          value={l.stage}
                          onChange={e => handleStageChange(l, e.target.value)}
                          style={{ border:'none', cursor:'pointer', fontFamily:'var(--font)', fontSize:11, fontWeight:500, padding:'2px 8px', borderRadius:20, appearance:'auto' }}
                        >
                          {STAGES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={{ fontVariantNumeric:'tabular-nums' }}>{fmt(l.estimated_value)}</td>
                      <td>{l.assigned_to}</td>
                      <td>
                        {l.requirements ? (
                          <span className={l.requirements_done ? 'badge badge-green' : 'badge badge-amber'}>
                            {l.requirements_done ? 'Done' : l.requirements_due ? `Due ${l.requirements_due}` : 'Pending'}
                          </span>
                        ) : <span style={{ color:'var(--text3)', fontSize:12 }}>—</span>}
                      </td>
                      <td style={{ color:'var(--text2)' }}>{l.follow_up_date || '—'}</td>
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn" style={{ padding:'4px 10px', fontSize:12 }} onClick={() => openEdit(l)}>Edit</button>
                          <button className="btn btn-danger" style={{ padding:'4px 10px', fontSize:12 }} onClick={() => remove(l.id)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Edit / Add modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>{editing ? 'Edit lead' : 'Add lead'}</h3>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-field"><label>Client name *</label><input value={form.client_name} onChange={e => setForm({...form, client_name:e.target.value})} /></div>
                <div className="form-field"><label>Contact person</label><input value={form.contact} onChange={e => setForm({...form, contact:e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-field"><label>Service interest</label><input value={form.service_interest} onChange={e => setForm({...form, service_interest:e.target.value})} /></div>
                <div className="form-field"><label>Estimated value (₹)</label><input type="number" value={form.estimated_value} onChange={e => setForm({...form, estimated_value:e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-field"><label>Stage</label>
                  <select value={form.stage} onChange={e => setForm({...form, stage:e.target.value})}>
                    {STAGES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-field"><label>Assigned to</label><input value={form.assigned_to} onChange={e => setForm({...form, assigned_to:e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-field"><label>Follow-up date</label><input type="date" value={form.follow_up_date} onChange={e => setForm({...form, follow_up_date:e.target.value})} /></div>
                <div className="form-field"><label>Remarks</label><input value={form.remarks} onChange={e => setForm({...form, remarks:e.target.value})} /></div>
              </div>
              {/* Requirements section */}
              <div style={{ marginTop:4, paddingTop:12, borderTop:'1px solid var(--border)' }}>
                <div style={{ fontSize:12, fontWeight:600, marginBottom:8, color:'var(--text2)' }}>Requirements</div>
                <div className="form-field"><label>Requirements (strategy docs, audits, etc.)</label>
                  <textarea rows={2} value={form.requirements || ''} onChange={e => setForm({...form, requirements:e.target.value})} style={{ resize:'vertical' }} placeholder="Describe what needs to be prepared..." />
                </div>
                <div className="form-row">
                  <div className="form-field"><label>Due date</label><input type="date" value={form.requirements_due || ''} onChange={e => setForm({...form, requirements_due:e.target.value})} /></div>
                  <div className="form-field" style={{ display:'flex', alignItems:'center', gap:8, paddingTop:24 }}>
                    <input type="checkbox" checked={form.requirements_done || false} onChange={e => setForm({...form, requirements_done:e.target.checked})} id="req-done" />
                    <label htmlFor="req-done" style={{ margin:0, fontSize:13 }}>Requirements completed</label>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.client_name}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add lead'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conversion modal — uses AddServiceModal to enforce client+brand+service linking */}
      {convertModal && convertLead && !convertSuccess && (
        <AddServiceModal
          onClose={() => setConvertModal(false)}
          onSaved={onServiceCreated}
        />
      )}
      {convertModal && convertSuccess && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setConvertModal(false)}>
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3>Project Created!</h3>
              <button className="modal-close" onClick={() => setConvertModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{convertLead.client_name}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>Service created successfully. Visit Marketing, Development, or Hosting to manage it.</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setConvertModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
